import { useCallback, useSyncExternalStore } from 'react';
import { MAX_WHEELS, type PublicArea, type PublicService, type PublicTenant } from '../../shared/public-tenant';
import type { Booking, Selection } from '../../shared/api/rpc';
import { areaByCode, defaultArea, rentalOptions } from '../../shared/areas';

/**
 * Черновик записи: выбор авто и услуг, время, контакты. Общий для калькулятора и
 * шторки записи; переживает перезагрузку страницы (sessionStorage), чтобы
 * случайное обновление не стирало введённое.
 */
export type Draft = {
  /** Код раздела; null — раздел по умолчанию (withDefaults подставит). */
  area: string | null;
  vehicleClass: string | null;
  radius: number | null;
  main: string | null;
  addons: string[];
  flags: Record<string, boolean>;
  /** Число колёс для поштучных услуг (perWheel). */
  wheels: number;
  /** Время аренды (раздел-аренда), минуты. */
  minutes: number | null;
  date: string | null;
  start: string | null;
  name: string;
  phone: string;
  car: string;
  consent: boolean;
};

export const EMPTY_DRAFT: Draft = {
  area: null,
  vehicleClass: null,
  radius: null,
  main: null,
  addons: [],
  flags: {},
  wheels: 1,
  minutes: null,
  date: null,
  start: null,
  name: '',
  phone: '',
  car: '',
  consent: false,
};

const drafts = new Map<string, Draft>();
const listeners = new Set<() => void>();
const storageKey = (slug: string) => `shinka:draft:${slug}`;

function load(slug: string): Draft {
  const cached = drafts.get(slug);
  if (cached) return cached;
  let draft = EMPTY_DRAFT;
  try {
    const raw = sessionStorage.getItem(storageKey(slug));
    if (raw) draft = { ...EMPTY_DRAFT, ...(JSON.parse(raw) as Partial<Draft>) };
  } catch {
    draft = EMPTY_DRAFT;
  }
  drafts.set(slug, draft);
  return draft;
}

function save(slug: string, draft: Draft): void {
  drafts.set(slug, draft);
  try {
    // согласие не сохраняем: его каждый раз подтверждают заново
    sessionStorage.setItem(storageKey(slug), JSON.stringify({ ...draft, consent: false }));
  } catch {
    // приватный режим — черновик живёт до перезагрузки
  }
  listeners.forEach((l) => l());
}

export function useDraft(slug: string): [Draft, (patch: Partial<Draft>) => void, () => void] {
  const draft = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => load(slug),
    () => EMPTY_DRAFT,
  );
  const update = useCallback((patch: Partial<Draft>) => save(slug, { ...load(slug), ...patch }), [slug]);
  const reset = useCallback(() => {
    const d = load(slug);
    // раздел, авто и контакты оставляем — при следующей записи не вводить заново
    save(slug, {
      ...EMPTY_DRAFT,
      area: d.area,
      vehicleClass: d.vehicleClass,
      radius: d.radius,
      name: d.name,
      phone: d.phone,
      car: d.car,
    });
  }, [slug]);
  return [draft, update, reset];
}

/**
 * Смена раздела: выбор услуг, особенностей, времени и длительности — заново (у раздела свои
 * услуги и посты); тип авто и радиус остаются — машина та же.
 */
export function areaPatch(code: string): Partial<Draft> {
  return { area: code, main: null, addons: [], flags: {}, minutes: null, start: null };
}

/** Раздел черновика среди видимых клиенту; устаревший код (раздел выключили) — null. */
export function draftArea(draft: Pick<Draft, 'area'>, tenant: Pick<PublicTenant, 'areas'>): PublicArea | null {
  return areaByCode(tenant, draft.area) ?? null;
}

/** Услуга доступна типу авто: её не скрыли для него (excludeClasses). */
export function serviceForClass(s: Pick<PublicService, 'excludeClasses'>, vehicleClass: string | null): boolean {
  return vehicleClass === null || !s.excludeClasses.includes(vehicleClass);
}

/** Число колёс из черновика: целое 1…MAX_WHEELS, иначе 1 (старый черновик без поля). */
export function draftWheels(draft: Pick<Draft, 'wheels'>): number {
  const w = draft.wheels;
  return Number.isInteger(w) && w >= 1 && w <= MAX_WHEELS ? w : 1;
}

/**
 * Значения по умолчанию: раздел — из черновика, если он ещё виден клиенту, иначе первый по
 * порядку (выбор услуг и время при этом сбрасываются). Дальше по виду раздела:
 * - шиномонтаж: первый тип авто, R16 (или ближайший), первая основная услуга раздела, доступная
 *   этому типу авто (скрытые для него услуги снимаются);
 * - услуги: первый тип авто, первая основная услуга раздела (радиус не нужен, но не стирается);
 * - аренда: первое подходящее время.
 */
export function withDefaults(draft: Draft, tenant: PublicTenant): Draft {
  const kept = draftArea(draft, tenant);
  const area = kept ?? defaultArea(tenant);
  let base = draft;
  if (area && !kept) {
    // раздел не выбран — просто подставить; выбранный раздел пропал — выбор в нём сбросить
    base = draft.area === null ? { ...draft, area: area.code } : { ...draft, ...areaPatch(area.code) };
  }

  const classes = tenant.pricing.vehicleClasses;
  const radii = tenant.pricing.radii;
  const vehicleClass =
    base.vehicleClass && classes.some((c) => c.code === base.vehicleClass)
      ? base.vehicleClass
      : (classes[0]?.code ?? null);
  const radius =
    base.radius && radii.includes(base.radius)
      ? base.radius
      : radii.includes(16)
        ? 16
        : (radii[Math.floor(radii.length / 2)] ?? null);

  if (area?.kind === 'rental') {
    const options = rentalOptions(area);
    const minutes = base.minutes !== null && options.includes(base.minutes) ? base.minutes : (options[0] ?? null);
    return { ...base, vehicleClass, radius, main: null, addons: [], flags: {}, minutes };
  }

  // шиномонтаж и услуги; без разделов (старый ответ) — прайс точки
  const services = (area ? area.services : tenant.pricing.services).filter((s) => serviceForClass(s, vehicleClass));
  const mains = services.filter((s) => s.kind === 'main');
  const main = base.main && mains.some((s) => s.code === base.main) ? base.main : (mains[0]?.code ?? null);
  const addons = base.addons.filter((code) => {
    const s = services.find((x) => x.code === code && x.kind === 'addon');
    return s && (s.onlyWith.length === 0 || (main !== null && s.onlyWith.includes(main)));
  });
  const flags = area?.kind === 'services' ? {} : base.flags;
  return { ...base, vehicleClass, radius, main, addons, flags, wheels: draftWheels(base), minutes: null };
}

/**
 * Выбор для сервера (quote, слоты, запись) по виду раздела черновика; всегда с area.
 * null — выбор неполный или раздел не виден клиенту.
 */
export function selectionOf(draft: Draft, tenant: Pick<PublicTenant, 'areas'>): Selection | null {
  const area = draftArea(draft, tenant);
  if (!area) return null;
  if (area.kind === 'rental') {
    return draft.minutes ? { area: area.code, minutes: draft.minutes } : null;
  }
  if (!draft.vehicleClass) return null;
  const services = [...(draft.main ? [draft.main] : []), ...draft.addons];
  if (services.length === 0) return null;
  if (area.kind === 'services') return { area: area.code, vehicleClass: draft.vehicleClass, services };
  if (!draft.radius) return null;
  const flags = Object.fromEntries(Object.entries(draft.flags).filter(([, v]) => v));
  // число колёс — только если выбрана поштучная услуга
  const perWheel = area.services.some((s) => s.perWheel && services.includes(s.code));
  return {
    area: area.code,
    vehicleClass: draft.vehicleClass,
    radius: draft.radius,
    services,
    flags,
    ...(perWheel ? { wheels: draftWheels(draft) } : {}),
  };
}

/**
 * Выбор для слотов переноса существующей записи — по виду её раздела, всегда с reschedule:
 * сервер показывает слоты и в выключенном разделе, а у аренды не сверяет время записи с текущими
 * min/max/шагом раздела. Запись старого сервера без раздела — шиномонтаж.
 */
export function rescheduleSelection(
  b: Pick<Booking, 'area' | 'minutes' | 'durationMin' | 'vehicleClass' | 'radius' | 'services' | 'flags' | 'items'>,
): Selection {
  const area = b.area;
  if (area?.kind === 'rental') {
    return { area: area.code, minutes: b.minutes ?? b.durationMin, reschedule: true };
  }
  if (area?.kind === 'services') {
    return { area: area.code, vehicleClass: b.vehicleClass ?? '', services: b.services, reschedule: true };
  }
  return {
    ...(area ? { area: area.code } : {}),
    vehicleClass: b.vehicleClass ?? '',
    radius: b.radius ?? 0,
    services: b.services,
    flags: b.flags,
    // число колёс записи — в её позициях (qty поштучной услуги)
    ...wheelsOf(b.items),
    reschedule: true,
  };
}

/** Число колёс из позиций записи или номера очереди: {wheels} или {}, если поштучных нет. */
export function wheelsOf(items: Pick<Booking['items'][number], 'qty'>[]): { wheels?: number } {
  const qty = items.find((i) => typeof i.qty === 'number')?.qty;
  return typeof qty === 'number' ? { wheels: qty } : {};
}
